import { Tooltip } from "@gotts.ai/ui";
import { useState } from "react";

import { TokenIcon } from "../../components/TokenIcon";
import { useDeployment } from "../../context";
import { TOKEN_DECIMALS, ZERO_ADDRESS } from "../../lib/constants";
import { formatFeePercent, formatLiquidity } from "../../lib/format";

import { resolveSymbol, tickToPrice } from "./types";

import type { V4Position } from "./types";
import type { Address } from "viem";

interface V4PositionsTableProps {
  positions: V4Position[];
  poolTicks?: Record<string, number>;
  loading?: boolean;
  onRemove?: (position: V4Position) => void;
  isExecuting?: boolean;
}

export function V4PositionsTable({
  positions,
  poolTicks,
  loading,
  onRemove,
  isExecuting,
}: V4PositionsTableProps) {
  const { deployment } = useDeployment();
  const tokens = (deployment?.tokens ?? {}) as unknown as Record<
    string,
    Address
  >;
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="space-y-3">
        <div className="skeleton skeleton-row rounded" />
        <div className="skeleton skeleton-row rounded" />
      </div>
    );
  }

  if (positions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded border border-dashed border-[var(--glass-border)] py-12 text-center">
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1"
          className="mb-3 text-[var(--color-text-muted)] opacity-40"
        >
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <line x1="12" y1="8" x2="12" y2="16" />
          <line x1="8" y1="12" x2="16" y2="12" />
        </svg>
        <span className="text-sm text-[var(--color-text-muted)]">
          No V4 positions
        </span>
        <span className="mt-1 text-xs text-[var(--color-text-muted)] opacity-60">
          Add liquidity above to get started
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="lp-info-banner">
        <svg
          className="lp-info-banner-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
        V4 positions support hooks and custom logic. Fees accrue separately.
      </div>

      {/* Position cards */}
      <div className="space-y-3">
        {positions.map((pos) => {
          const sym0 = resolveSymbol(pos.currency0, tokens);
          const sym1 = resolveSymbol(pos.currency1, tokens);
          const dec0 = TOKEN_DECIMALS[sym0] ?? 18;
          const dec1 = TOKEN_DECIMALS[sym1] ?? 18;
          const isFullRange =
            pos.tickLower <= -887220 && pos.tickUpper >= 887220;
          const priceLow = tickToPrice(pos.tickLower, dec0, dec1);
          const priceHigh = tickToPrice(pos.tickUpper, dec0, dec1);

          // Determine in-range status
          let inRange: boolean | undefined = undefined;
          if (poolTicks) {
            const key = `${pos.currency0.toLowerCase()}:${pos.currency1.toLowerCase()}:${String(pos.fee)}`;
            const tick = poolTicks[key];
            if (tick !== undefined) {
              inRange = pos.tickLower <= tick && tick < pos.tickUpper;
            }
          }

          const hookDisplay = pos.hooks;
          const hasHook =
            hookDisplay.toLowerCase() !== ZERO_ADDRESS.toLowerCase();
          const isExpanded = expandedId === pos.id;

          const rangeClass =
            inRange === true
              ? "lp-card-in-range"
              : inRange === false
                ? "lp-card-out-of-range"
                : "";

          return (
            <div key={pos.id} className={`lp-card lp-card-v4 ${rangeClass}`}>
              {/* Header row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="lp-pair-icons">
                    <TokenIcon symbol={sym0} size={32} />
                    <TokenIcon symbol={sym1} size={32} />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-mono text-sm font-semibold text-[var(--color-gold)]">
                      {sym0}/{sym1}
                    </span>
                    <div className="mt-0.5 flex items-center gap-2">
                      <span
                        className="protocol-pill protocol-pill-v4"
                        style={{ fontSize: 9, padding: "1px 6px" }}
                      >
                        V4
                      </span>
                      <span className="lp-fee-pill">
                        {formatFeePercent(pos.fee)}
                      </span>
                      {hasHook && (
                        <Tooltip content={hookDisplay}>
                          <span className="cursor-help font-mono text-[9px] text-[var(--color-in-range)]">
                            Hook
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {inRange === true && (
                    <span className="badge-in-range">
                      <span className="range-dot range-dot-in" />
                      In Range
                    </span>
                  )}
                  {inRange === false && (
                    <span className="badge-out-of-range">
                      <span className="range-dot range-dot-out" />
                      Out of Range
                    </span>
                  )}
                  {inRange === undefined && (
                    <span className="badge-full-range">
                      {isFullRange ? "Full Range" : "Unknown"}
                    </span>
                  )}
                </div>
              </div>

              {/* Price range + liquidity row */}
              <div className="mt-4 grid grid-cols-2 gap-3">
                {/* Price range */}
                <div className="lp-price-range">
                  {isFullRange ? (
                    <div className="flex w-full items-center justify-center py-1">
                      <span className="badge-position-type">Full Range</span>
                    </div>
                  ) : (
                    <>
                      <div className="lp-price-range-bound">
                        <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
                          Min
                        </span>
                        <span className="font-mono text-xs text-[var(--color-text)]">
                          {priceLow}
                        </span>
                      </div>
                      <span className="lp-price-range-arrow">{"\u2192"}</span>
                      <div className="lp-price-range-bound">
                        <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
                          Max
                        </span>
                        <span className="font-mono text-xs text-[var(--color-text)]">
                          {priceHigh}
                        </span>
                      </div>
                    </>
                  )}
                </div>

                {/* Liquidity value */}
                <div className="flex flex-col items-end justify-center">
                  <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
                    Liquidity
                  </span>
                  <Tooltip content={pos.liquidity.toString()}>
                    <span className="cursor-help font-mono text-sm font-medium text-[var(--color-text)]">
                      {formatLiquidity(pos.liquidity)}
                    </span>
                  </Tooltip>
                </div>
              </div>

              {/* Hook info (if present) */}
              {hasHook && (
                <div className="mt-3 flex items-center justify-between rounded-sm bg-[rgba(45,212,191,0.03)] px-3 py-2">
                  <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
                    Hook Contract
                  </span>
                  <Tooltip content={hookDisplay}>
                    <span className="cursor-help font-mono text-xs text-[var(--color-in-range)]">
                      {hookDisplay.slice(0, 6)}...{hookDisplay.slice(-4)}
                    </span>
                  </Tooltip>
                </div>
              )}

              {/* Actions */}
              <div className="lp-actions">
                <button
                  type="button"
                  className="btn-ghost rounded-sm px-3 text-[10px]"
                  onClick={() => {
                    setExpandedId(isExpanded ? null : pos.id);
                  }}
                >
                  {isExpanded ? "Hide Details" : "Details"}
                </button>
                <div className="flex-1" />
                {onRemove && (
                  <button
                    type="button"
                    className="btn-remove-destructive"
                    onClick={() => {
                      onRemove(pos);
                    }}
                    disabled={isExecuting}
                  >
                    Remove
                  </button>
                )}
              </div>

              {/* Expandable details */}
              <div
                className="lp-detail-expand"
                data-open={isExpanded ? "true" : "false"}
              >
                <div>
                  <div className="lp-detail-grid">
                    <div className="lp-detail-item">
                      <span className="lp-detail-item-label">Position ID</span>
                      <span className="lp-detail-item-value">{pos.id}</span>
                    </div>
                    <div className="lp-detail-item">
                      <span className="lp-detail-item-label">
                        Liquidity (raw)
                      </span>
                      <span className="lp-detail-item-value">
                        {pos.liquidity.toString()}
                      </span>
                    </div>
                    <div className="lp-detail-item">
                      <span className="lp-detail-item-label">Tick Lower</span>
                      <span className="lp-detail-item-value">
                        {pos.tickLower.toLocaleString()}
                      </span>
                    </div>
                    <div className="lp-detail-item">
                      <span className="lp-detail-item-label">Tick Upper</span>
                      <span className="lp-detail-item-value">
                        {pos.tickUpper.toLocaleString()}
                      </span>
                    </div>
                    <div className="lp-detail-item">
                      <span className="lp-detail-item-label">Currency 0</span>
                      <span className="lp-detail-item-value">
                        {pos.currency0}
                      </span>
                    </div>
                    <div className="lp-detail-item">
                      <span className="lp-detail-item-label">Currency 1</span>
                      <span className="lp-detail-item-value">
                        {pos.currency1}
                      </span>
                    </div>
                    <div className="lp-detail-item">
                      <span className="lp-detail-item-label">Fee</span>
                      <span className="lp-detail-item-value">{pos.fee}</span>
                    </div>
                    <div className="lp-detail-item">
                      <span className="lp-detail-item-label">Hook</span>
                      <span className="lp-detail-item-value">
                        {hookDisplay}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
